import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTeam } from '@/hooks/useTeam';
import { Loader2 } from 'lucide-react';
import type { WonOpportunityData } from '@/hooks/useOpportunities';

interface WonOpportunityDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: WonOpportunityData) => void;
  opportunityTitle?: string;
  initialProduct?: string;
  initialPrice?: number;
}

export function WonOpportunityDialog({
  open,
  onClose,
  onConfirm,
  opportunityTitle,
  initialProduct,
  initialPrice
}: WonOpportunityDialogProps) {
  const { members, loading: loadingMembers } = useTeam();
  const [product, setProduct] = useState('');
  const [price, setPrice] = useState('');
  const [saleDate, setSaleDate] = useState('');
  const [selectedMemberId, setSelectedMemberId] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setProduct(initialProduct || '');
      setPrice(initialPrice ? String(initialPrice) : '');
      setSaleDate(new Date().toISOString().split('T')[0]);
      setSelectedMemberId('');
      setNotes('');
    }
  }, [open, initialProduct, initialPrice]);

  const activeMembers = members.filter(m => m.is_active);

  const handleConfirm = async () => {
    setIsSubmitting(true);

    const data: WonOpportunityData = {
      product: product.trim() || undefined,
      price: price ? parseFloat(price) : undefined,
      sale_date: saleDate ? new Date(saleDate).toISOString() : undefined,
      won_by_user_id: selectedMemberId || undefined,
      notes: notes.trim() || undefined
    };

    onConfirm(data);
    setIsSubmitting(false);
    handleClose();
  };

  const handleClose = () => {
    setProduct('');
    setPrice('');
    setSaleDate('');
    setSelectedMemberId('');
    setNotes('');
    onClose();
  };

  const formatCurrency = (value: string) => {
    const numericValue = value.replace(/\D/g, '');
    if (!numericValue) return '';
    const number = parseInt(numericValue, 10) / 100;
    return number.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\D/g, '');
    if (!rawValue) {
      setPrice('');
      return;
    }
    const numericValue = parseInt(rawValue, 10) / 100;
    setPrice(String(numericValue));
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#18181B] border-[#3F3F464D] max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white">Dados da Venda</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {opportunityTitle && (
            <p className="text-sm text-neutral-400">
              Registrar venda para: <span className="text-white font-medium">{opportunityTitle}</span>
            </p>
          )}

          <div className="space-y-2">
            <Label className="text-white">Produto</Label>
            <Input
              value={product}
              onChange={(e) => setProduct(e.target.value)}
              placeholder="Nome do produto ou servico"
              className="bg-neutral-600 text-white border-[#3F3F46] placeholder:text-gray-400"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-white">Valor (R$)</Label>
            <Input
              value={price ? formatCurrency(String(Math.round(parseFloat(price) * 100))) : ''}
              onChange={handlePriceChange}
              placeholder="0,00"
              className="bg-neutral-600 text-white border-[#3F3F46] placeholder:text-gray-400"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-white">Data da Venda</Label>
            <Input
              type="date"
              value={saleDate}
              onChange={(e) => setSaleDate(e.target.value)}
              className="bg-neutral-600 text-white border-[#3F3F46] [color-scheme:dark]"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-white">Membro da Equipe</Label>
            <Select value={selectedMemberId} onValueChange={setSelectedMemberId} disabled={loadingMembers}>
              <SelectTrigger className="w-full bg-neutral-600 text-white border-[#3F3F46]">
                <SelectValue placeholder={loadingMembers ? "Carregando..." : "Selecione um membro"} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {activeMembers.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.name || member.email}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-white">Anotacao</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Observacoes sobre a venda..."
              className="bg-neutral-600 text-white border-[#3F3F46] placeholder:text-gray-400 min-h-[80px] resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            className="border-white text-white hover:bg-white hover:text-black"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isSubmitting}
            className="bg-green-600 hover:bg-green-700"
          >
            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar Venda'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
